import {ActionFunction} from '../interface/action/action-function.type';
import {invariant} from '../util/debug/invariant.util';
import {ActionEnum} from '../enum/action.enum';
import {isStringEmpty} from '../util/string/is-string-empty.util';
import {getOnlyNumber} from '../util/string/get-only-number.util';
import {
  ICallUrlPayload,
  IOpenNewTabPayload,
  IOpenPopupWindowPayload,
  IRedirectPagePayload,
  ISendEmailPayload
} from '../interface/action/browser-action.interface';
import {correctUrl} from '../util/url/correct-url.util';
import {
  POPUP_LARGE_WINDOW_HEIGHT,
  POPUP_LARGE_WINDOW_WIDTH,
  POPUP_WINDOW_HEIGHT,
  POPUP_WINDOW_WIDTH
} from '../constants';
import {PopupSizeEnum} from '../enum/popup-size.enum';

export const openNewTab$: ActionFunction<IOpenNewTabPayload> = action$ => {
  return action$
    .type(ActionEnum.OPEN_NEW_TAB)
    .end(({url}) => {
      invariant(!isStringEmpty(url), 'Invalid url provided');
      window.open(correctUrl(url), '_blank');
    });
};

export const sendEmail$: ActionFunction<ISendEmailPayload> = action$ => {
  return action$
    .type(ActionEnum.SEND_EMAIL)
    .end(({email, subject, body}) => {
      invariant(email !== undefined, 'Invalid mail provided');
      let mailString = `mailto:${email}`;
      if (!isStringEmpty(subject) || !isStringEmpty(body)) {
        mailString += '?';
      }
      if (!isStringEmpty(subject)) {
        mailString += `subject=${subject}&`;
      }
      if (!isStringEmpty(body)) {
        mailString += `body=${body}`;
      }
      window.location.assign(mailString);
    });
};

export const redirectPage$: ActionFunction<IRedirectPagePayload> = (action$, {history}) => {
  return action$
    .type(ActionEnum.REDIRECT_PAGE)
    .end(({uri}) => {
      invariant(uri !== undefined, 'Invalid ui provided');
      let realURI = uri;
      if (realURI.indexOf('http') === 0) {
        window.location.assign(realURI);
        return;
      }
      if (realURI[ 0 ] !== '/') {
        realURI = '/' + realURI;
      }
      history.push(realURI);
    });
};

export const replacePage$: ActionFunction<IRedirectPagePayload> = (action$, {history}) => {
  return action$
    .type(ActionEnum.REPLACE_PAGE)
    .end(({uri}) => {
      invariant(uri !== undefined, 'Invalid ui provided');
      let realURI = uri;
      if (realURI[ 0 ] !== '/') {
        realURI = '/' + realURI;
      }
      history.replace(realURI);
    });
};

export const getPhoneCallURL = (phoneNumber, areaCode = '+46', isMobile = false) => {
  if (phoneNumber === undefined) {
    return '#';
  }
  if (phoneNumber[ 0 ] === '+') {
    areaCode = '+';
  }

  return (isMobile ? 'tel:' : 'callto:') + areaCode + getOnlyNumber(phoneNumber);
};

export const callUrl$: ActionFunction<ICallUrlPayload> = (action$, {state}) => {
  return action$
    .type(ActionEnum.CALL_URL)
    .end(({phoneNumber, areaCode}) => {
      invariant(!isStringEmpty(phoneNumber), 'Invalid phone provided');
      window.location.assign(getPhoneCallURL(phoneNumber, areaCode, state.configuration.isMobile));
    });
};

export const openPopupWindow$: ActionFunction<IOpenPopupWindowPayload> = action$ => {
  return action$
    .type(ActionEnum.OPEN_POPUP_WINDOW)
    .end(({url, title, popupSize}) => {
      invariant(!isStringEmpty(url), 'Invalid url provided');

      const w = popupSize === PopupSizeEnum.NORMAL ? POPUP_WINDOW_WIDTH : POPUP_LARGE_WINDOW_WIDTH;
      const h = popupSize === PopupSizeEnum.NORMAL ? POPUP_WINDOW_HEIGHT : POPUP_LARGE_WINDOW_HEIGHT;
      // https://stackoverflow.com/questions/4068373/center-a-popup-window-on-screen
      // Fixes dual-screen position                         Most browsers      Firefox
      const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
      const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;

      const width = window.innerWidth ? window.innerWidth : document.documentElement.clientWidth ? document.documentElement.clientWidth : screen.width;
      const height = window.innerHeight ? window.innerHeight : document.documentElement.clientHeight ? document.documentElement.clientHeight : screen.height;

      const left = ((width / 2) - (w / 2)) + dualScreenLeft;
      const top = ((height / 2) - (h / 2)) + dualScreenTop;
      const newWindow = window.open(url, title, 'scrollbars=yes, width=' + w + ', height=' + h + ', top=' + top + ', left=' + left);

      // Puts focus on the newWindow
      if (window.focus) {
        newWindow.focus();
      }
    });
};

export default {
  actions: [
    openNewTab$,
    sendEmail$,
    callUrl$,
    openPopupWindow$,
    redirectPage$,
    replacePage$
  ]
};
